import { useState } from 'react';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Case } from '@/api/types/backend-dto-types';
import { Icon } from '@/new/components/icon';
import { Input } from '@/new/components/shadcn/input';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/new/components/shadcn/tooltip';
import { KimMode } from '@/types';

interface ExampleKimQuestionProps {
  text: string;
  type: 'question' | 'draft';
  onClick: () => void;
}

export function ExampleKimQuestion({ text, type, onClick }: ExampleKimQuestionProps) {
  const isQuestion = type === 'question';

  return (
    <Tooltip>
      <TooltipTrigger
        onClick={onClick}
        className={`flex w-full cursor-pointer items-center gap-1.5 truncate rounded py-1.5 text-xs transition-all duration-300 hover:bg-gray-100`}
      >
        {isQuestion ? (
          <Icon className="lucide--lightbulb h-3.5 w-3.5 shrink-0 text-blue-400" />
        ) : (
          <Icon className="lucide--file-text h-3.5 w-3.5 shrink-0 text-green-400" />
        )}
        <span className="truncate">{text}</span>
      </TooltipTrigger>
      <TooltipContent sideOffset={8} className="bg-gray-700 text-white">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

interface KimOverviewProps {
  caseObject: Case | undefined;
  onAskQuestion: (question: string, mode: KimMode) => void;
}

export const KimOverview = ({ caseObject, onAskQuestion }: KimOverviewProps) => {
  const [question, setQuestion] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    if (question.trim()) {
      onAskQuestion(question, KimMode.QuestionFlow);
      trackCustomEvent(TrackingEvents.AskKimFromSummaryQuestion);
      setQuestion('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="space-y-4 lg:col-span-3">
      <div className="relative flex items-center">
        <Input
          type="text"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={'Ask Kim a question about this subset...'}
          className="w-full rounded-lg border border-gray-300 px-4 py-3 pr-12 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none"
        />
        <button
          onClick={handleSubmit}
          className="bg-brandSecondary hover:bg-brandSecondary-hover absolute right-2 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full px-[9px] py-2 text-sm font-semibold text-white disabled:bg-gray-500"
        >
          <Icon className="lucide--arrow-right h-4 w-4 shrink-0 text-white" />
        </button>
      </div>

      {caseObject &&
        Array.isArray(caseObject?.exampleKimQuestions?.question) &&
        caseObject.exampleKimQuestions.question.length > 0 && (
          <div className="mt-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <h4 className="mb-1 text-xs font-semibold text-gray-700">Example case analysis questions:</h4>
                <div className="space-y-0.75">
                  {caseObject.exampleKimQuestions.question.map((q: any, idx: number) => (
                    <ExampleKimQuestion
                      key={idx}
                      text={typeof q === 'string' ? q : q.prompt}
                      type="question"
                      onClick={() => {
                        onAskQuestion(q.prompt, KimMode.QuestionFlow);
                        trackCustomEvent(TrackingEvents.AskKimFromSummaryQuestion);
                      }}
                    />
                  ))}
                </div>
              </div>

              {caseObject &&
                Array.isArray(caseObject.exampleKimQuestions.draft) &&
                caseObject.exampleKimQuestions.draft.length > 0 && (
                  <div>
                    <h4 className="mb-1 text-xs font-semibold text-gray-700">Example draft outputs:</h4>
                    <div className="space-y-0.75">
                      {caseObject.exampleKimQuestions.draft.map((d: any, idx: number) => (
                        <ExampleKimQuestion
                          key={idx}
                          text={typeof d === 'string' ? d : d.prompt}
                          type="draft"
                          onClick={() => {
                            onAskQuestion(d.prompt, KimMode.DraftFlow);
                            trackCustomEvent(TrackingEvents.AskKimFromSummaryDraft);
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
    </div>
  );
};
